import Big from 'big.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useFormik } from 'formik';
import { cloneDeep, get, isEqual, merge, pick, set } from 'lodash';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useParams } from 'react-router-dom';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import {
  ExtendStringProps,
  TaxReport,
  TaxReportCapitalGains,
  TaxReportCredits,
  TaxReportForeignIncome,
  TaxReportInfo,
  TaxReportOffsets,
  TaxReportOtherDeductions,
  TaxReportOtherNonAssessableAmounts,
  TaxReportShareOfIncome,
  TaxReportSmallBusinessIncomeTaxOffset,
  TrustEntityDetails,
} from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import { getFinancialYearRangeByYear } from '@/modules/issuer-dashboard/helpers/taxReportYears.ts';
import {
  TAX_REPORT_INCOME_HOLDER_KEY,
  useGetTaxReportIncomeHolderQuery,
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import {
  Alert,
  BodyCell,
  Button,
  CalendarInput,
  CalendarInputProps,
  CustomCheckbox,
  SelectButtonOption,
  SelectButtons,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  getSelectOptionByEnum,
} from 'ui';
import { AlertBlueIcon } from 'ui/icons';
import { getDisabledDate } from 'ui/utils';
import { UpdateEntityRequest } from 'api';

dayjs.extend(utc);

const funcMinus = (first: number, second: number): string => {
  return new Big(first).minus(second).round(2).valueOf();
};

const collectPaths = (obj: any, prefix = ''): string[] => {
  const paths = Object.keys(obj).flatMap(key => {
    const value = obj[key];
    const path = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return collectPaths(value, path);
    }

    return [path];
  });

  return paths;
};

function fillAllocationDefaults<T extends object>(obj: T): T {
  const clone = cloneDeep(obj);
  const forbiddenSuffixes = ['_allocation_method', '_total_fund', '_total_value'];
  const wrongSuffixes = [
    '_allocation_method_allocation_method',
    '_total_fund_allocation_method',
    '_total_value_allocation_method',
    '_allocation_method_total_fund',
    '_allocation_method_total_value',
    '_total_fund_total_fund',
    '_total_fund_total_value',
    '_total_value_total_fund',
    '_total_value_total_value',
  ];
  function recurse(curr: any) {
    if (!curr || typeof curr !== 'object') return;
    for (const key of Object.keys(curr)) {
      const val = curr[key];

      if (wrongSuffixes.some(suffix => key.endsWith(suffix))) {
        delete curr[key];
        if (key.endsWith('_allocation_method_allocation_method')) {
          const baseKey = key.split('_allocation_method')[0];
          curr[`${baseKey}_allocation_method`] = AllocationMethod.Manual;
        }
        continue;
      }

      if (val && typeof val === 'object' && !Array.isArray(val)) {
        recurse(val);
      }

      const isBaseField =
        (typeof val === 'string' || val === undefined) &&
        !forbiddenSuffixes.some(suffix => key.endsWith(suffix));

      if (isBaseField) {
        const allocKey = `${key}_allocation_method`;
        if (!(allocKey in curr)) {
          curr[allocKey] = AllocationMethod.Manual;
        }
        const totalFundKey = `${key}_total_fund`;
        if (!(totalFundKey in curr)) {
          curr[totalFundKey] = 0;
        }
        const totalValueKey = `${key}_total_value`;
        if (!(totalValueKey in curr)) {
          curr[totalValueKey] = 0;
        }
        const baseKey = `${key}`;
        if (!(baseKey in curr)) {
          curr[baseKey] = 0;
        }
      }
    }
  }

  recurse(clone);
  return clone;
}

function fillAllTotalFunds<T extends object>(obj: T, totalFund: number): T {
  const clone = cloneDeep(obj);
  const recurse = (curr: any) => {
    if (!curr || typeof curr !== 'object') return;
    for (const key of Object.keys(curr)) {
      const val = curr[key];
      if (key.endsWith('_total_fund')) {
        curr[key] = totalFund;
        continue;
      }
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        recurse(val);
      }
    }
  };
  recurse(clone);
  return clone;
}

export function getLastFinancialYearRange() {
  const currentYear = dayjs.utc().year();
  return getFinancialYearRangeByYear(currentYear);
}

type TypeHeadTaxReport =
  | 'Label'
  | 'Total fund'
  | 'Tax Return'
  | 'Allocation method'
  | 'Total value'
  | 'Balance to distributed';

const headItems: TableHeadColumn<TypeHeadTaxReport>[] = [
  { title: 'Label', className: 'w-[340px] min-w-[340px] max-w-[340px]' },
  { title: 'Total fund' },
  { title: 'Tax Return' },
  { title: 'Allocation method' },
  { title: 'Total value' },
  { title: 'Balance to distributed', className: 'min-w-[170px]' },
];

const renderLabelCell = (label: string) => (
  <div className="w-[340px] min-w-[340px] max-w-[340px]">
    <span className="text-sm leading-[22px] break-words">{label}</span>
  </div>
);

enum AllocationMethod {
  'Auto' = 'auto',
  'Manual' = 'manual',
}

interface TaxReportItemField {
  label: string;
  fieldName: string;
  word: string;
}

interface ExternalTaxReportHolderData {
  entityId: number;
  year: number;
  entityName?: string;
  initialTaxReportJson?: Record<string, unknown>;
  isSaving?: boolean;
  registerTotalFund?: number;
  onSave?: (taxReportJson: Record<string, unknown>) => Promise<void> | void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const validationSchema = yup.object<TaxReport>({
  tax_report: yup.object<ExtendStringProps<TaxReportInfo>>({
    share_of_income: yup.object<TaxReportShareOfIncome>({
      primary_production: yup.string().trim(),
      pp_ncmi: yup.string().trim(),
      pp_excluded_ncmi: yup.string().trim(),
      non_primary_production: yup.string().trim(),
      npp_ncmi: yup.string().trim(),
      npp_excluded_ncmi: yup.string().trim(),
    }),
    credits: yup.object<TaxReportCredits>({
      credit_abn_not_quoted: yup.string().trim(),
      franked_distributions: yup.string().trim(),
      franking_credit: yup.string().trim(),
      tfn_amounts_withheld: yup.string().trim(),
      share_tfn_withheld_trusts: yup.string().trim(),
      credit_foreign_resident_withholding: yup.string().trim(),
      share_foreign_resident_cgt_withholding: yup.string().trim(),
      franking_credits_nz: yup.string().trim(),
    }),
    capital_gains: yup.object<TaxReportCapitalGains>({
      capital: yup.string().trim(),
      discounted: yup.string().trim(),
      other: yup.string().trim(),
      gross: yup.string().trim(),
      losses_applied: yup.string().trim(),
      discounts_applied: yup.string().trim(),
      small_business_concessions: yup.string().trim(),
      ncmi_capital_gains: yup.string().trim(),
      excluded_ncmi_capital_gains: yup.string().trim(),
    }),
    foreign_income: yup.object<TaxReportForeignIncome>({
      attributed_foreign_income: yup.string().trim(),
      other_foreign_income: yup.string().trim(),
      foreign_tax_offset: yup.string().trim(),
    }),
    offsets: yup.object<TaxReportOffsets>({
      rental_affordability_scheme: yup.string().trim(),
      exploration_credits: yup.string().trim(),
      venture_capital_offset: yup.string().trim(),
      early_stage_investor_offset: yup.string().trim(),
    }),
    small_business_income_tax_offset: yup.object<TaxReportSmallBusinessIncomeTaxOffset>({
      share_net_small_business_income: yup.string().trim(),
    }),
    other_non_assessable_amounts: yup.object<TaxReportOtherNonAssessableAmounts>({
      net_exempt_income: yup.string().trim(),
      non_assessable_non_exempt_amount: yup.string().trim(),
      tax_free_amounts: yup.string().trim(),
      tax_deferred_amounts: yup.string().trim(),
      return_of_capital: yup.string().trim(),
      gross_cash_distribution: yup.string().trim(),
    }),
    other_deductions: yup.object<TaxReportOtherDeductions>({
      tfn_amounts_withheld: yup.string().trim(),
      non_resident_amounts_withheld: yup.string().trim(),
      other_expenses: yup.string().trim(),
      net_cash_distribution: yup.string().trim(),
    }),
    div6aa_eligible_income: yup.string().trim(),
    share_of_income_trust_estate: yup.string().trim(),
    assessment_calculation_code: yup.string().trim(),
  }),
});

const initialValues: ExtendStringProps<TaxReportInfo> = {
  capital_gains: {
    capital: '0',
    discounted: '0',
    other: '0',
    gross: '0',
    losses_applied: '0',
    discounts_applied: '0',
    small_business_concessions: '0',
    ncmi_capital_gains: '0',
    excluded_ncmi_capital_gains: '0',
  },
  credits: {
    credit_abn_not_quoted: '0',
    franked_distributions: '0',
    franking_credit: '0',
    tfn_amounts_withheld: '0',
    share_tfn_withheld_trusts: '0',
    credit_foreign_resident_withholding: '0',
    share_foreign_resident_cgt_withholding: '0',
    franking_credits_nz: '0',
  },
  div6aa_eligible_income: '0',
  share_of_income_trust_estate: '0',
  assessment_calculation_code: '0',
  foreign_income: {
    attributed_foreign_income: '0',
    other_foreign_income: '0',
    foreign_tax_offset: '0',
  },
  offsets: {
    rental_affordability_scheme: '0',
    exploration_credits: '0',
    venture_capital_offset: '0',
    early_stage_investor_offset: '0',
  },
  share_of_income: {
    primary_production: '0',
    pp_ncmi: '0',
    pp_excluded_ncmi: '0',
    non_primary_production: '0',
    npp_ncmi: '0',
    npp_excluded_ncmi: '0',
  },
  small_business_income_tax_offset: {
    share_net_small_business_income: '0',
  },
  other_non_assessable_amounts: {
    net_exempt_income: '0',
    non_assessable_non_exempt_amount: '0',
    tax_free_amounts: '0',
    tax_deferred_amounts: '0',
    return_of_capital: '0',
    gross_cash_distribution: '0',
  },
  other_deductions: {
    tfn_amounts_withheld: '0',
    non_resident_amounts_withheld: '0',
    other_expenses: '0',
    net_cash_distribution: '0',
  },
};

export const TaxReportHolder = ({
  moveForward,
  entity_type_prefix,
  person_details_key,
  index,
  allowSkip,
  externalData,
}: OnboardingStepProps & { index?: number; externalData?: ExternalTaxReportHolderData }) => {
  const isExternalMode = !!externalData;
  const [autoDistributeAllEnabled, setAutoDistributeAllEnabled] = useState(false);
  const [hasUserChanges, setHasUserChanges] = useState(false);
  const didApplyInitialBalanceRef = useRef(false);
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    isExternalMode || !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );
  const [date, setDate] = useState(
    isExternalMode
      ? getFinancialYearRangeByYear(externalData?.year || dayjs.utc().year())
      : getLastFinancialYearRange(),
  );
  const { data: dataIncome, isFetching: isFetchingDate } = useGetTaxReportIncomeHolderQuery(
    {
      entity_id: isExternalMode
        ? externalData?.entityId || 0
        : (dataHolder?.data?.old_data?.entity_id && +dataHolder?.data?.old_data?.entity_id) || 0,
      date_from: date.date_from ? dayjs.utc(date.date_from).toISOString() : undefined,
      date_to: date.date_to ? dayjs.utc(date.date_to).toISOString() : undefined,
    },
    (isExternalMode && !(externalData?.entityId && externalData?.year)) ||
      (!isExternalMode && !dataHolder?.data?.old_data?.entity_id) ||
      !date?.date_from ||
      !date?.date_to,
  );

  const generateInitialValues = useCallback((): TaxReport => {
    if (isExternalMode) {
      const isEmptyExternalJson =
        !externalData?.initialTaxReportJson ||
        (typeof externalData.initialTaxReportJson === 'object' &&
          !Array.isArray(externalData.initialTaxReportJson) &&
          !Object.keys(externalData.initialTaxReportJson).length);

      const tax_report = omit(
        isEmptyExternalJson
          ? fillAllTotalFunds(
              fillAllocationDefaults(
                merge({}, initialValues, externalData?.initialTaxReportJson || {}),
              ),
              Number(externalData?.registerTotalFund || 0),
            )
          : fillAllocationDefaults(
              merge({}, initialValues, externalData?.initialTaxReportJson || {}),
            ),
        ['date_from_allocation_method', 'date_to_allocation_method'],
      );
      return { tax_report } as TaxReport;
    }
    const entityTypeDetails = get(
      dataHolder?.data?.new_data?.entity_data || {},
      entity_type_prefix,
      {},
    ) as TrustEntityDetails;

    if (person_details_key && (index || index === 0)) {
      const personDetails = entityTypeDetails?.[person_details_key as 'settlors']?.find(
        (_: any, indexOfPerson: number) => indexOfPerson === index,
      ) as TaxReport;

      const tax_report = omit(
        fillAllocationDefaults(merge({}, initialValues, personDetails?.tax_report)),
        ['date_from_allocation_method', 'date_to_allocation_method'],
      );

      return {
        ...personDetails,
        tax_report,
      };
    }
    const tax_report = omit(
      fillAllocationDefaults(merge({}, initialValues, entityTypeDetails?.tax_report)),
      ['date_from_allocation_method', 'date_to_allocation_method'],
    );

    return {
      ...entityTypeDetails,
      tax_report,
    };
  }, [
    dataHolder?.data?.new_data?.entity_data,
    entity_type_prefix,
    index,
    isExternalMode,
    person_details_key,
    externalData?.initialTaxReportJson,
    externalData?.registerTotalFund,
  ]);

  const generatePayload = (values: TaxReport) => {
    const entityTypeDetails = dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix];

    if (person_details_key && (index || index === 0)) {
      return {
        ...dataHolder?.data?.new_data?.entity_data,
        [entity_type_prefix]: {
          ...entityTypeDetails,
          [person_details_key]: entityTypeDetails[person_details_key].map(
            (shareholder: any, indexOfShareholder: number) =>
              indexOfShareholder === index ? { ...shareholder, ...values } : shareholder,
          ),
        },
      };
    }

    return {
      ...dataHolder?.data?.new_data?.entity_data,
      [entity_type_prefix]: {
        ...entityTypeDetails,
        ...values,
      },
    };
  };

  const formik = useFormik<TaxReport>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: generateInitialValues(),
    onSubmit: async values => {
      if (isExternalMode) {
        await externalData?.onSave?.(values.tax_report as Record<string, unknown>);
        formik.resetForm({ values });
        return;
      }
      const init = pick<TaxReport, keyof TaxReport>(values, ['tax_report']);
      const entityTypeDetails = dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix];
      let personDetails: TaxReport | null = null;
      if (person_details_key && (index || index === 0)) {
        personDetails = entityTypeDetails?.[person_details_key]?.find(
          (_: any, indexOfPerson: number) => indexOfPerson === index,
        ) as TaxReport;
      }
      const current = pick<TaxReport, keyof TaxReport>(personDetails || entityTypeDetails, [
        'tax_report',
      ]);
      if (!isEqual(init, current)) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: generatePayload(values) as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });
  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });
  const hasUnsavedChanges =
    !isExternalMode && formik.dirty && hasUserChanges && !formik.isSubmitting;
  const leaveMessage =
    'Do you really want to leave this form? You will lose some entered information.';
  const blocker = useBlocker(hasUnsavedChanges);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    if (window.confirm(leaveMessage)) {
      blocker.proceed();
      return;
    }

    blocker.reset();
  }, [blocker, leaveMessage]);

  useEffect(() => {
    if (!isExternalMode) return;
    externalData?.onDirtyChange?.(formik.dirty && hasUserChanges && !formik.isSubmitting);
  }, [externalData, formik.dirty, formik.isSubmitting, hasUserChanges, isExternalMode]);

  useEffect(() => {
    if (isExternalMode) return;
    const entityTypeDetails = get(
      dataHolder?.data?.new_data?.entity_data || {},
      entity_type_prefix,
      {},
    ) as TrustEntityDetails;

    if (person_details_key && (index || index === 0)) {
      const personDetails = entityTypeDetails?.[person_details_key as 'settlors']?.find(
        (_: any, indexOfPerson: number) => indexOfPerson === index,
      ) as TaxReport;

      if (personDetails?.tax_report?.date_from && personDetails?.tax_report?.date_to) {
        setDate({
          date_from: personDetails?.tax_report?.date_from,
          date_to: personDetails?.tax_report?.date_to,
        });
      }
      return;
    }
    if (entityTypeDetails?.tax_report?.date_from && entityTypeDetails?.tax_report?.date_to) {
      setDate({
        date_from: entityTypeDetails?.tax_report?.date_from,
        date_to: entityTypeDetails?.tax_report?.date_to,
      });
    }
  }, [dataHolder?.data, entity_type_prefix, index, isExternalMode, person_details_key]);

  const handleReportRangeDateChange: CalendarInputProps['rangeOnChange'] = dates => {
    setHasUserChanges(true);

    if (dates) {
      const start_date = dayjs.utc(dates[0]?.startOf('day')).toISOString();
      const end_date = dayjs.utc(dates[1]?.endOf('day')).toISOString();

      formik.setFieldValue('tax_report.date_from', start_date);
      formik.setFieldError('tax_report.date_from', '');
      formik.setFieldValue('tax_report.date_to', end_date);
      formik.setFieldError('tax_report.date_to', '');

      setDate({
        date_from: start_date,
        date_to: end_date,
      });
      return;
    }
    formik.setFieldValue('tax_report.date_from', '');
    formik.setFieldError('tax_report.date_from', '');
    formik.setFieldValue('tax_report.date_to', '');
    formik.setFieldError('tax_report.date_to', '');
    setDate({
      date_from: '',
      date_to: '',
    });
    queryClient.resetQueries({ queryKey: [TAX_REPORT_INCOME_HOLDER_KEY] });
  };

  const resolveSourceTotals = useCallback(() => {
    const totalFundRaw = isExternalMode
      ? externalData?.registerTotalFund
      : dataIncome?.all_investors_distributions;
    const totalValueRaw = dataIncome?.all_other_investors_distributions;

    const totalFund = Number(totalFundRaw);
    const totalValue = Number(totalValueRaw);

    if (!Number.isFinite(totalFund) || !Number.isFinite(totalValue)) return null;
    return { totalFund, totalValue };
  }, [
    dataIncome?.all_investors_distributions,
    dataIncome?.all_other_investors_distributions,
    externalData?.registerTotalFund,
    isExternalMode,
  ]);

  const recalculateRow = useCallback(
    (
      basePath: string,
      currentTaxReport: ExtendStringProps<TaxReportInfo>,
      sourceTotals?: { totalFund: number; totalValue: number } | null,
    ): Partial<ExtendStringProps<TaxReportInfo>> => {
      const next: Partial<ExtendStringProps<TaxReportInfo>> = {};
      const rowTotalFund =
        sourceTotals?.totalFund ?? Number(get(currentTaxReport, `${basePath}_total_fund`));
      const rowTotalValue =
        sourceTotals?.totalValue ?? Number(get(currentTaxReport, `${basePath}_total_value`));

      if (!Number.isFinite(rowTotalFund) || !Number.isFinite(rowTotalValue)) return next;

      set(next, `${basePath}_total_fund`, new Big(rowTotalFund).round(2).valueOf());
      set(next, `${basePath}_total_value`, new Big(rowTotalValue).round(2).valueOf());
      set(next, basePath, funcMinus(rowTotalFund, rowTotalValue));
      return next;
    },
    [],
  );

  const updateRowsByAllocationMethod = useCallback(
    (
      currentTaxReport: ExtendStringProps<TaxReportInfo>,
      allocationMethod: AllocationMethod,
      sourceTotals?: { totalFund: number; totalValue: number } | null,
    ) => {
      const updates: Partial<ExtendStringProps<TaxReportInfo>> = {};
      const allPaths = collectPaths(currentTaxReport);

      allPaths.forEach(path => {
        if (!path.endsWith('_allocation_method')) return;
        const basePath = path.replace(/_allocation_method$/, '');
        set(updates, path, allocationMethod);

        if (allocationMethod === AllocationMethod.Auto && sourceTotals) {
          merge(updates, recalculateRow(basePath, currentTaxReport, sourceTotals));
          return;
        }

        const rowTotalFund = Number(get(currentTaxReport, `${basePath}_total_fund`));
        if (Number.isFinite(rowTotalFund)) {
          set(updates, `${basePath}_total_fund`, new Big(rowTotalFund).round(2).valueOf());
          set(updates, `${basePath}_total_value`, 0);
          set(updates, basePath, funcMinus(rowTotalFund, 0));
        }
      });

      const nextTaxReport = merge({}, currentTaxReport, updates);
      formik.setValues({ tax_report: nextTaxReport });
    },
    [formik, recalculateRow],
  );

  const applyInitialBalanceFromTotals = useCallback(
    (currValue: ExtendStringProps<TaxReportInfo>) => {
      const updates: Partial<ExtendStringProps<TaxReportInfo>> = {};
      const allPaths = collectPaths(currValue);

      allPaths.forEach(path => {
        if (!path.endsWith('_total_fund')) return;

        const basePath = path.replace(/_total_fund$/, '');
        const totalFund = Number(get(currValue, `${basePath}_total_fund`));
        const totalValue = Number(get(currValue, `${basePath}_total_value`));
        const currentBalance = get(currValue, basePath);

        if (!Number.isFinite(totalFund) || !Number.isFinite(totalValue)) return;

        if (currentBalance === '' || currentBalance === null || currentBalance === undefined) {
          set(updates, basePath, funcMinus(totalFund, totalValue));
          return;
        }

        if (Number(currentBalance) === 0 && (totalFund !== 0 || totalValue !== 0)) {
          set(updates, basePath, funcMinus(totalFund, totalValue));
        }
      });

      if (!Object.keys(updates).length) return null;

      const nextTaxReport = merge({}, currValue, updates);
      return nextTaxReport;
    },
    [],
  );

  useEffect(() => {
    if (didApplyInitialBalanceRef.current) return;
    if (formik.dirty) return;

    const nextTaxReport = applyInitialBalanceFromTotals(formik.values.tax_report || {});
    if (nextTaxReport) {
      formik.resetForm({
        values: {
          tax_report: nextTaxReport,
        },
      });
    }
    didApplyInitialBalanceRef.current = true;
  }, [applyInitialBalanceFromTotals, formik.dirty, formik.values.tax_report]);

  const optionsAllocationMethod = useMemo(
    () => getSelectOptionByEnum<AllocationMethod>(AllocationMethod),
    [],
  );

  const handleChangeAllocationMethodValue = useCallback(
    (field: string, value: AllocationMethod) => {
      setHasUserChanges(true);
      const currentTaxReport = formik.values.tax_report || {};
      formik.setFieldValue(field, value);
      formik.setFieldError(field, '');
      const currentValue = get(formik.values, field);
      if (currentValue === value) return;
      const fullBaseField = field.split('_allocation_method')[0];
      const baseField = fullBaseField.startsWith('tax_report.')
        ? fullBaseField.replace('tax_report.', '')
        : fullBaseField;
      if (value === AllocationMethod.Manual) {
        const rowTotalFund = Number(get(currentTaxReport, `${baseField}_total_fund`));
        const safeTotalFund = Number.isFinite(rowTotalFund) ? rowTotalFund : 0;
        const updates: Partial<ExtendStringProps<TaxReportInfo>> = {};
        set(updates, `${baseField}_allocation_method`, AllocationMethod.Manual);
        set(updates, `${baseField}_total_fund`, new Big(safeTotalFund).round(2).valueOf());
        set(updates, `${baseField}_total_value`, 0);
        set(updates, baseField, funcMinus(safeTotalFund, 0));
        formik.setValues({
          tax_report: merge({}, currentTaxReport, updates),
        });
        return;
      }

      const totals = resolveSourceTotals();
      const rowUpdates = recalculateRow(baseField, currentTaxReport, totals);
      const updates: Partial<ExtendStringProps<TaxReportInfo>> = {};
      set(updates, `${baseField}_allocation_method`, AllocationMethod.Auto);
      merge(updates, rowUpdates);
      formik.setValues({
        tax_report: merge({}, currentTaxReport, updates),
      });
    },
    [formik, recalculateRow, resolveSourceTotals],
  );

  const handleToggleAutoDistributeAll = useCallback(
    (checked: boolean) => {
      setHasUserChanges(true);
      setAutoDistributeAllEnabled(checked);
      const currentTaxReport = formik.values.tax_report || {};
      if (!checked) {
        updateRowsByAllocationMethod(currentTaxReport, AllocationMethod.Manual);
        return;
      }

      updateRowsByAllocationMethod(currentTaxReport, AllocationMethod.Auto, resolveSourceTotals());
    },
    [formik.values.tax_report, resolveSourceTotals, updateRowsByAllocationMethod],
  );

  const handleOnChangeTotalFund = useCallback(
    (fieldName: string) => (value: number | string) => {
      setHasUserChanges(true);
      const totalFundField = `${fieldName}_total_fund`;
      const totalValueField = `${fieldName}_total_value`;
      const allocationField = `${fieldName}_allocation_method`;
      formik.setFieldValue(totalFundField, value);
      formik.setFieldError(totalFundField, '');
      formik.setFieldValue(allocationField, AllocationMethod.Manual);
      formik.setFieldError(allocationField, '');

      const currentTotalValue = get(formik.values, totalValueField);
      const parsedTotalFund = Number(value);
      const parsedTotalValue = Number(currentTotalValue);
      if (Number.isFinite(parsedTotalFund) && Number.isFinite(parsedTotalValue)) {
        formik.setFieldValue(fieldName, funcMinus(parsedTotalFund, parsedTotalValue));
        formik.setFieldError(fieldName, '');
      }
    },
    [formik.values],
  );

  const handleOnChangeTotalValue = useCallback(
    (fieldName: string) => (value: number | string) => {
      setHasUserChanges(true);
      const totalFundField = `${fieldName}_total_fund`;
      const totalValueField = `${fieldName}_total_value`;
      const allocationField = `${fieldName}_allocation_method`;
      formik.setFieldValue(totalValueField, value);
      formik.setFieldError(totalValueField, '');
      formik.setFieldValue(allocationField, AllocationMethod.Manual);
      formik.setFieldError(allocationField, '');

      const currentTotalFund = get(formik.values, totalFundField);
      const parsedTotalFund = Number(currentTotalFund);
      const parsedTotalValue = Number(value);
      if (Number.isFinite(parsedTotalFund) && Number.isFinite(parsedTotalValue)) {
        formik.setFieldValue(fieldName, funcMinus(parsedTotalFund, parsedTotalValue));
        formik.setFieldError(fieldName, '');
      }
    },
    [formik.values],
  );

  const handleOnChangeBalance = useCallback(
    (fieldName: string) => (value: number | string) => {
      setHasUserChanges(true);
      formik.setFieldValue(fieldName, value);
      formik.setFieldError(fieldName, '');
      const allocationField = `${fieldName}_allocation_method`;
      formik.setFieldValue(allocationField, AllocationMethod.Manual);
      formik.setFieldError(allocationField, '');
    },
    [],
  );

  const bodyItemsShareOfIncome = useMemo<TableBodyColumn[]>(() => {
    const items: TaxReportItemField[] = [
      {
        label: 'Primary production',
        fieldName: 'tax_report.share_of_income.primary_production',
        word: 'A',
      },
      {
        label: 'PP - NCMI',
        fieldName: 'tax_report.share_of_income.pp_ncmi',
        word: 'A1',
      },
      {
        label: 'PP - Excluded from NCMI',
        fieldName: 'tax_report.share_of_income.pp_excluded_ncmi',
        word: 'A2',
      },
      {
        label: 'Non-primary production',
        fieldName: 'tax_report.share_of_income.non_primary_production',
        word: 'B',
      },
      {
        label: 'NPP - NCMI',
        fieldName: 'tax_report.share_of_income.npp_ncmi',
        word: 'B1',
      },
      {
        label: 'NPP - Excluded from NCMI',
        fieldName: 'tax_report.share_of_income.npp_excluded_ncmi',
        word: 'B2',
      },
      {
        label: 'Assessment calculation code',
        fieldName: 'tax_report.assessment_calculation_code',
        word: 'V',
      },
      {
        label: 'Share of income of the trust estate',
        fieldName: 'tax_report.share_of_income_trust_estate',
        word: 'W',
      },
      {
        label: 'Credit for tax withheld – foreign resident withholding (excluding capital gains)',
        fieldName: 'tax_report.credits.credit_foreign_resident_withholding',
        word: 'L',
      },
      {
        label: 'Australian franking credits from a New Zealand franking company',
        fieldName: 'tax_report.credits.franking_credits_nz',
        word: 'N',
      },
    ];

    return items?.map(item => {
      const body: BodyCell[] = [
        {
          cell: renderLabelCell(item.label),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_fund`}
              value={get(formik.values, `${item.fieldName}_total_fund`)}
              onChange={handleOnChangeTotalFund(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: (
            <div className="w-[80px] min-w-[80px] pl-4 py-1 h-8 rounded-r-full text-white bg-black">
              {item.word}
            </div>
          ),
        },
        {
          cell: (
            <div className="min-w-[140px] min-h-9 w-[140px] h-9">
              <SelectButtons<AllocationMethod>
                options={optionsAllocationMethod}
                selected={
                  findDefaultValue(
                    optionsAllocationMethod,
                    get(
                      formik.values,
                      `${item.fieldName}_allocation_method`,
                    ) as unknown as AllocationMethod,
                  ) as SelectButtonOption<AllocationMethod>
                }
                onChange={selected =>
                  handleChangeAllocationMethodValue(`${item.fieldName}_allocation_method`, selected)
                }
              />
            </div>
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_value`}
              value={get(formik.values, `${item.fieldName}_total_value`)}
              onChange={handleOnChangeTotalValue(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}`}
              value={get(formik.values, `${item.fieldName}`)}
              onChange={handleOnChangeBalance(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
      ];

      return {
        cells: body,
      };
    });
  }, [
    formik.values,
    handleChangeAllocationMethodValue,
    handleOnChangeBalance,
    handleOnChangeTotalFund,
    handleOnChangeTotalValue,
    optionsAllocationMethod,
  ]);

  const bodyItemsCapitalGains = useMemo<TableBodyColumn[]>(() => {
    const items: TaxReportItemField[] = [
      {
        label: 'Capital gains',
        fieldName: 'tax_report.capital_gains.capital',
        word: 'F',
      },
      {
        label: 'Discounted capital gains (Net)',
        fieldName: 'tax_report.capital_gains.discounted',
        word: '',
      },
      {
        label: 'Other capital gains',
        fieldName: 'tax_report.capital_gains.other',
        word: '',
      },
      {
        label: 'Gross capital gain',
        fieldName: 'tax_report.capital_gains.gross',
        word: 'F1',
      },
      {
        label: 'Capital losses applied',
        fieldName: 'tax_report.capital_gains.losses_applied',
        word: 'F2',
      },
      {
        label: 'CGT discounts applied',
        fieldName: 'tax_report.capital_gains.discounts_applied',
        word: 'F3',
      },
      {
        label: 'CGT small business concessions applied',
        fieldName: 'tax_report.capital_gains.small_business_concessions',
        word: 'F4',
      },
      {
        label: 'NCMI capital gains',
        fieldName: 'tax_report.capital_gains.ncmi_capital_gains',
        word: 'F5',
      },
      {
        label: 'Excluded from NCMI capital gains',
        fieldName: 'tax_report.capital_gains.excluded_ncmi_capital_gains',
        word: 'F6',
      },
    ];

    return items?.map(item => {
      const body: BodyCell[] = [
        {
          cell: renderLabelCell(item.label),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_fund`}
              value={get(formik.values, `${item.fieldName}_total_fund`)}
              onChange={handleOnChangeTotalFund(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: item.word && (
            <div className="w-[80px] min-w-[80px] pl-4 py-1 h-8 rounded-r-full text-white bg-black">
              {item.word}
            </div>
          ),
        },
        {
          cell: (
            <div className="min-w-[140px] min-h-9 w-[140px] h-9">
              <SelectButtons<AllocationMethod>
                options={optionsAllocationMethod}
                selected={
                  findDefaultValue(
                    optionsAllocationMethod,
                    get(
                      formik.values,
                      `${item.fieldName}_allocation_method`,
                    ) as unknown as AllocationMethod,
                  ) as SelectButtonOption<AllocationMethod>
                }
                onChange={selected =>
                  handleChangeAllocationMethodValue(`${item.fieldName}_allocation_method`, selected)
                }
              />
            </div>
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_value`}
              value={get(formik.values, `${item.fieldName}_total_value`)}
              onChange={handleOnChangeTotalValue(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}`}
              value={get(formik.values, `${item.fieldName}`)}
              onChange={handleOnChangeBalance(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
      ];

      return {
        cells: body,
      };
    });
  }, [
    formik.values,
    handleChangeAllocationMethodValue,
    handleOnChangeBalance,
    handleOnChangeTotalFund,
    handleOnChangeTotalValue,
    optionsAllocationMethod,
  ]);

  const bodyItemsForeignIncomeOffsets = useMemo<TableBodyColumn[]>(() => {
    const items: TaxReportItemField[] = [
      {
        label: 'Attributed foreign income',
        fieldName: 'tax_report.foreign_income.attributed_foreign_income',
        word: 'G',
      },
      {
        label: 'Other assessable foreign source income',
        fieldName: 'tax_report.foreign_income.other_foreign_income',
        word: 'H',
      },
      {
        label: 'Foreign income tax offset',
        fieldName: 'tax_report.foreign_income.foreign_tax_offset',
        word: 'I',
      },
      {
        label: 'Share of national rental affordability scheme tax offset',
        fieldName: 'tax_report.offsets.rental_affordability_scheme',
        word: 'R',
      },
      {
        label: 'Exploration credits distributed',
        fieldName: 'tax_report.offsets.exploration_credits',
        word: 'M',
      },
      {
        label: 'Early stage venture capital limited partnership tax offset',
        fieldName: 'tax_report.offsets.venture_capital_offset',
        word: 'T',
      },
      {
        label: 'Early stage investor tax offset',
        fieldName: 'tax_report.offsets.early_stage_investor_offset',
        word: 'J',
      },
      {
        label: 'Div 6AA Eligible income',
        fieldName: 'tax_report.div6aa_eligible_income',
        word: 'C1',
      },
      {
        label: 'Share of net small business income',
        fieldName: 'tax_report.small_business_income_tax_offset.share_net_small_business_income',
        word: 'Y',
      },
    ];

    return items?.map(item => {
      const body: BodyCell[] = [
        {
          cell: renderLabelCell(item.label),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_fund`}
              value={get(formik.values, `${item.fieldName}_total_fund`)}
              onChange={handleOnChangeTotalFund(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: item.word && (
            <div className="w-[80px] min-w-[80px] pl-4 py-1 h-8 rounded-r-full text-white bg-black">
              {item.word}
            </div>
          ),
        },
        {
          cell: (
            <div className="min-w-[140px] min-h-9 w-[140px] h-9">
              <SelectButtons<AllocationMethod>
                options={optionsAllocationMethod}
                selected={
                  findDefaultValue(
                    optionsAllocationMethod,
                    get(
                      formik.values,
                      `${item.fieldName}_allocation_method`,
                    ) as unknown as AllocationMethod,
                  ) as SelectButtonOption<AllocationMethod>
                }
                onChange={selected =>
                  handleChangeAllocationMethodValue(`${item.fieldName}_allocation_method`, selected)
                }
              />
            </div>
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_value`}
              value={get(formik.values, `${item.fieldName}_total_value`)}
              onChange={handleOnChangeTotalValue(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}`}
              value={get(formik.values, `${item.fieldName}`)}
              onChange={handleOnChangeBalance(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
      ];

      return {
        cells: body,
      };
    });
  }, [
    formik.values,
    handleChangeAllocationMethodValue,
    handleOnChangeBalance,
    handleOnChangeTotalFund,
    handleOnChangeTotalValue,
    optionsAllocationMethod,
  ]);

  const bodyItemsCredits = useMemo<TableBodyColumn[]>(() => {
    const items: TaxReportItemField[] = [
      {
        label: 'Credit for tax withheld where ABN not quoted',
        fieldName: 'tax_report.credits.credit_abn_not_quoted',
        word: 'C',
      },
      {
        label: 'TFN amounts withheld',
        fieldName: 'tax_report.credits.tfn_amounts_withheld',
        word: 'E',
      },
      {
        label: 'Franking credit',
        fieldName: 'tax_report.credits.franking_credit',
        word: 'D',
      },
      {
        label: 'Franked distributions',
        fieldName: 'tax_report.credits.franked_distributions',
        word: 'U',
      },
      {
        label: 'Share of credit for TFN amounts withheld from payments from closely held trusts',
        fieldName: 'tax_report.credits.share_tfn_withheld_trusts',
        word: 'O',
      },
      {
        label: 'Share of credit for foreign resident capital gains withholding amounts',
        fieldName: 'tax_report.credits.share_foreign_resident_cgt_withholding',
        word: 'Z',
      },
      // {
      //   label: 'Australian franking credits from a New Zealand franking company',
      //   fieldName: 'tax_report.credits.franking_credits_nz',
      //   word: 'N',
      // },
    ];

    return items?.map(item => {
      const body: BodyCell[] = [
        {
          cell: renderLabelCell(item.label),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_fund`}
              value={get(formik.values, `${item.fieldName}_total_fund`)}
              onChange={handleOnChangeTotalFund(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: item.word && (
            <div className="w-[80px] min-w-[80px] pl-4 py-1 h-8 rounded-r-full text-white bg-black">
              {item.word}
            </div>
          ),
        },
        {
          cell: (
            <div className="min-w-[140px] min-h-9 w-[140px] h-9">
              <SelectButtons<AllocationMethod>
                options={optionsAllocationMethod}
                selected={
                  findDefaultValue(
                    optionsAllocationMethod,
                    get(
                      formik.values,
                      `${item.fieldName}_allocation_method`,
                    ) as unknown as AllocationMethod,
                  ) as SelectButtonOption<AllocationMethod>
                }
                onChange={selected =>
                  handleChangeAllocationMethodValue(`${item.fieldName}_allocation_method`, selected)
                }
              />
            </div>
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_value`}
              value={get(formik.values, `${item.fieldName}_total_value`)}
              onChange={handleOnChangeTotalValue(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}`}
              value={get(formik.values, `${item.fieldName}`)}
              onChange={handleOnChangeBalance(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
      ];

      return {
        cells: body,
      };
    });
  }, [
    formik.values,
    handleChangeAllocationMethodValue,
    handleOnChangeBalance,
    handleOnChangeTotalFund,
    handleOnChangeTotalValue,
    optionsAllocationMethod,
  ]);

  const bodyItemsOtherNonAssessable = useMemo<TableBodyColumn[]>(() => {
    const items: TaxReportItemField[] = [
      {
        label: 'Net exempt income',
        fieldName: 'tax_report.other_non_assessable_amounts.net_exempt_income',
        word: '',
      },
      {
        label: 'Non-assessable non exempt amount',
        fieldName: 'tax_report.other_non_assessable_amounts.non_assessable_non_exempt_amount',
        word: 'E',
      },
      {
        label: 'Tax free amounts',
        fieldName: 'tax_report.other_non_assessable_amounts.tax_free_amounts',
        word: '',
      },
      {
        label: 'Tax deferred amounts',
        fieldName: 'tax_report.other_non_assessable_amounts.tax_deferred_amounts',
        word: '',
      },
      {
        label: 'Return of capital',
        fieldName: 'tax_report.other_non_assessable_amounts.return_of_capital',
        word: '',
      },
      {
        label: 'Gross other distribution',
        fieldName: 'tax_report.other_non_assessable_amounts.gross_cash_distribution',
        word: 'F',
      },
    ];

    return items?.map(item => {
      const body: BodyCell[] = [
        {
          cell: renderLabelCell(item.label),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_fund`}
              value={get(formik.values, `${item.fieldName}_total_fund`)}
              onChange={handleOnChangeTotalFund(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: item.word && (
            <div className="w-[80px] min-w-[80px] pl-4 py-1 h-8 rounded-r-full text-white bg-black">
              {item.word}
            </div>
          ),
        },
        {
          cell: (
            <div className="min-w-[140px] min-h-9 w-[140px] h-9">
              <SelectButtons<AllocationMethod>
                options={optionsAllocationMethod}
                selected={
                  findDefaultValue(
                    optionsAllocationMethod,
                    get(
                      formik.values,
                      `${item.fieldName}_allocation_method`,
                    ) as unknown as AllocationMethod,
                  ) as SelectButtonOption<AllocationMethod>
                }
                onChange={selected =>
                  handleChangeAllocationMethodValue(`${item.fieldName}_allocation_method`, selected)
                }
              />
            </div>
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_value`}
              value={get(formik.values, `${item.fieldName}_total_value`)}
              onChange={handleOnChangeTotalValue(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}`}
              value={get(formik.values, `${item.fieldName}`)}
              onChange={handleOnChangeBalance(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
      ];

      return {
        cells: body,
      };
    });
  }, [
    formik.values,
    handleChangeAllocationMethodValue,
    handleOnChangeBalance,
    handleOnChangeTotalFund,
    handleOnChangeTotalValue,
    optionsAllocationMethod,
  ]);

  const bodyItemsOtherDeductions = useMemo<TableBodyColumn[]>(() => {
    const items: TaxReportItemField[] = [
      {
        label: 'Less: TFN amounts withheld',
        fieldName: 'tax_report.other_deductions.tfn_amounts_withheld',
        word: '13R',
      },
      {
        label: 'Less: Non-resident amounts withheld',
        fieldName: 'tax_report.other_deductions.non_resident_amounts_withheld',
        word: '13A',
      },
      {
        label: 'Less: Other expenses',
        fieldName: 'tax_report.other_deductions.other_expenses',
        word: '13Y',
      },
      {
        label: 'Net distribution during period',
        fieldName: 'tax_report.other_deductions.net_cash_distribution',
        word: '',
      },
    ];

    return items?.map(item => {
      const body: BodyCell[] = [
        {
          cell: renderLabelCell(item.label),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_fund`}
              value={get(formik.values, `${item.fieldName}_total_fund`)}
              onChange={handleOnChangeTotalFund(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: item.word && (
            <div className="w-[80px] min-w-[80px] pl-4 py-1 h-8 rounded-r-full text-white bg-black">
              {item.word}
            </div>
          ),
        },
        {
          cell: (
            <div className="min-w-[140px] min-h-9 w-[140px] h-9">
              <SelectButtons<AllocationMethod>
                options={optionsAllocationMethod}
                selected={
                  findDefaultValue(
                    optionsAllocationMethod,
                    get(
                      formik.values,
                      `${item.fieldName}_allocation_method`,
                    ) as unknown as AllocationMethod,
                  ) as SelectButtonOption<AllocationMethod>
                }
                onChange={selected =>
                  handleChangeAllocationMethodValue(`${item.fieldName}_allocation_method`, selected)
                }
              />
            </div>
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}_total_value`}
              value={get(formik.values, `${item.fieldName}_total_value`)}
              onChange={handleOnChangeTotalValue(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
        {
          cell: (
            <CurrencyInput
              name={`${item.fieldName}`}
              value={get(formik.values, `${item.fieldName}`)}
              onChange={handleOnChangeBalance(item.fieldName)}
              className="min-w-[140px] min-h-9 w-[160px] h-9"
              classNameCurrency="bottom-[6px]"
              withBorder={true}
            />
          ),
        },
      ];

      return {
        cells: body,
      };
    });
  }, [
    formik.values,
    handleChangeAllocationMethodValue,
    handleOnChangeBalance,
    handleOnChangeTotalFund,
    handleOnChangeTotalValue,
    optionsAllocationMethod,
  ]);

  const taxReportTitle = `${
    isExternalMode
      ? `${externalData?.entityName || ''} Tax Report Information`
      : `${dataHolder?.data?.new_data?.entity_data?.entity_name ?? ''} Tax Report Information`
  }`;

  return (
    <OnboardingHolderWrapper
      title={isExternalMode ? undefined : taxReportTitle}
      formik={formik}
      onClickContinue={isExternalMode ? undefined : formik.handleSubmit}
      buttonContinueName={isExternalMode ? 'Save' : 'Continue'}
      onClickSkip={
        allowSkip
          ? () => {
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isExternalMode ? externalData?.isSaving : isLoading}
    >
      <div className="w-full flex flex-col items-start gap-6">
        {isExternalMode && (
          <div className="w-full flex items-start justify-between gap-4">
            <span
              style={{ wordBreak: 'break-word' }}
              className="text-[18px] leading-6 font-[500] font-[Inter] break-words"
            >
              {taxReportTitle}
            </span>
            <Button
              className="w-[120px]"
              onClick={formik.handleSubmit}
              disabled={formik.isSubmitting || !!externalData?.isSaving}
            >
              Save
            </Button>
          </div>
        )}

        <Alert icon={AlertBlueIcon} variant="neutral-2" className="mb-0">
          <span className="w-full text-left text-blue-700 whitespace-normal">
            This entire step contains only information shown on Tax Return report. Depending on
            investor entity type, you need to fill out applicable fields and leave others empty.
            Refer to{' '}
            <a
              target="_blank"
              href="https://www.ato.gov.au/forms-and-instructions/amma-statement-and-standard-distribution-statement-2025-instructions/amma-statement-or-sds-example#PartAYourdetails10"
              className="text-blue-600 underline hover:cursor-pointer hover:no-underline"
            >
              ATO
            </a>{' '}
            to understand to what is applicable for this holder.
          </span>
        </Alert>

        {!isExternalMode && (
          <div className="flex gap-4 items-end">
            <CalendarInput
              name="time_frame"
              label="DATE RANGE (For automatic allocation method)"
              disabledDate={getDisabledDate}
              rangeOnChange={handleReportRangeDateChange}
              rangeValue={
                date.date_from && date.date_to ? [dayjs(date.date_from), dayjs(date.date_to)] : null
              }
              range={true}
            />
            {isFetchingDate && <Spinner size="md" className="m-0 mb-1" />}
          </div>
        )}

        <div className="w-full flex flex-col items-start gap-4">
          <div className="w-full flex items-center justify-between pl-4 pr-2">
            <span className="text-[16px] leading-6 font-medium font-[Inter]">Share of Income</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-700">
                Do you want to pre-fill balance to distributed based on distributions in our
                database?
              </span>
              <CustomCheckbox
                checked={autoDistributeAllEnabled}
                onChange={e => handleToggleAutoDistributeAll(e.target.checked)}
              />
            </div>
          </div>
          <Table
            className="my-0"
            withBorderX={false}
            classNameHead="border-t-transparent"
            headItems={headItems}
            items={bodyItemsShareOfIncome}
            withRounded={false}
          />
        </div>

        <div className="w-full flex flex-col items-start gap-4">
          <span className="text-[16px] leading-6 font-medium font-[Inter] pl-4">Capital Gains</span>
          <Table
            className="my-0"
            withBorderX={false}
            classNameHead="border-t-transparent"
            headItems={headItems}
            items={bodyItemsCapitalGains}
            withRounded={false}
          />
        </div>

        <div className="w-full flex flex-col items-start gap-4">
          <span className="text-[16px] leading-6 font-medium font-[Inter] pl-4">
            Foreign Income & Offsets
          </span>
          <Table
            className="my-0"
            withBorderX={false}
            classNameHead="border-t-transparent"
            headItems={headItems}
            items={bodyItemsForeignIncomeOffsets}
            withRounded={false}
          />
        </div>

        <div className="w-full flex flex-col items-start gap-4">
          <span className="text-[16px] leading-6 font-medium font-[Inter] pl-4">Credits</span>
          <Table
            className="my-0"
            withBorderX={false}
            classNameHead="border-t-transparent"
            headItems={headItems}
            items={bodyItemsCredits}
            withRounded={false}
          />
        </div>

        <div className="w-full flex flex-col items-start gap-4">
          <span className="text-[16px] leading-6 font-medium font-[Inter] pl-4">
            Other non-assessable amounts
          </span>
          <Table
            className="my-0"
            withBorderX={false}
            classNameHead="border-t-transparent"
            headItems={headItems}
            items={bodyItemsOtherNonAssessable}
            withRounded={false}
          />
        </div>

        <div className="w-full flex flex-col items-start gap-4">
          <span className="text-[16px] leading-6 font-medium font-[Inter] pl-4">
            Other deductions from distribution
          </span>
          <Table
            className="my-0"
            withBorderX={false}
            classNameHead="border-t-transparent"
            headItems={headItems}
            items={bodyItemsOtherDeductions}
            withRounded={false}
          />
        </div>
      </div>
    </OnboardingHolderWrapper>
  );
};
